/**
 * Date utility functions for formatting and handling dates across the application
 */

/**
 * Format a date range for display
 * @param startDate - Start date string or null
 * @param endDate - End date string or null
 * @param options - Formatting options
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  options: {
    fallback?: string;
    format?: 'short' | 'long' | 'numeric';
  } = {}
): string => {
  const { fallback = 'Not specified', format = 'short' } = options;

  if (!startDate || !endDate) {
    return fallback;
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return fallback;
    }

    let dateFormatOptions: Intl.DateTimeFormatOptions;
    
    switch (format) {
      case 'long':
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        };
        break;
      case 'numeric':
        dateFormatOptions = { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        };
        break;
      case 'short':
      default:
        dateFormatOptions = { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        };
        break;
    }

    const startFormatted = start.toLocaleDateString(undefined, dateFormatOptions);
    const endFormatted = end.toLocaleDateString(undefined, dateFormatOptions);

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    return fallback;
  }
};